import { __decorate } from "tslib";
import { BehaviorSubject, Subject } from "rxjs";
import { filter } from "rxjs/operators";
import { Alert, AlertType } from "./shared/models/alert";
import { Injectable, Injector, isDevMode } from "@angular/core";
import { NavigationStart, Router } from "@angular/router";
import { SessionStorageService } from "./shared/services/storage/session-storage.service";
import { SessionStorageEnum } from "./shared/services/storage/session-storage-constants";
import { HttpClient } from "@angular/common/http";
import { HttpStatusCodeEnum } from "./shared/models/http-status-code-enum.model";
import { SharedConstants } from "./shared/constants/shared.constants";
import { ApiApplicationStatusConstants } from "./shared/constants/api-constants/api-application-summary.constants";
import { UserInformationService } from "./shared/services/user/user-information.service";
let AppControllerService = class AppControllerService {
    router;
    http;
    injector;
    sessionStorage;
    fetchRecordCountValue = 50;
    landingPageShown = false;
    selectedApplicationID;
    selectedJobID = null;
    organisationsSubject = new BehaviorSubject([]);
    $organisationsSubject = this.organisationsSubject.asObservable();
    selectedSettingHeadingSubject = new BehaviorSubject("");
    $selectedSettingHeadingSubject = this.selectedSettingHeadingSubject.asObservable();
    alertSubject = new Subject();
    keepAfterRouteChange = false;
    config;
    _selectedModuleID;
    selectedOrgIdSubject = new BehaviorSubject(null);
    $selectedOrgIdSubject = this.selectedOrgIdSubject.asObservable();
    moduleSimulateModeSubject = new BehaviorSubject(false);
    $moduleSimulateModeSubject = this.moduleSimulateModeSubject.asObservable();
    moduleAllowResubmissionSubject = new BehaviorSubject(false);
    $moduleAllowResubmissionSubject = this.moduleAllowResubmissionSubject.asObservable();
    StepToLoad = new BehaviorSubject(undefined);
    $stepToLoad = this.StepToLoad.asObservable();
    isMobileDevice = new BehaviorSubject(false);
    $isMobileDevice = this.isMobileDevice.asObservable();
    isSmallMobileDevice = new BehaviorSubject(false);
    $isSmallMobileDevice = this.isSmallMobileDevice.asObservable();
    baseRequestObject = {
        authenticatedUser: {},
        moduleID: null,
        appID: null
    };
    _selectedOrganisationID;
    constructor(router, http, injector, sessionStorage) {
        this.router = router;
        this.http = http;
        this.injector = injector;
        this.sessionStorage = sessionStorage;
        // clear alert messages on route change unless 'keepAfterRouteChange' flag is true
        router.events.subscribe(event => {
            if (event instanceof NavigationStart) {
                if (this.keepAfterRouteChange) {
                    // only keep for a single route change
                    this.keepAfterRouteChange = false;
                }
                else {
                    // clear alert messages
                    setTimeout(() => {
                        this.clearMessage();
                    }, 5000);
                }
            }
        });
    }
    updateIsMobileDevice(value) {
        this.isMobileDevice.next(value);
    }
    updateIsSmallMobileDevice(value) {
        this.isSmallMobileDevice.next(value);
    }
    updateStepToLoad(value) {
        this.StepToLoad.next(value);
    }
    get selectedOrganisationId() {
        return this.selectedOrgIdSubject.value;
    }
    get moduleSimulateMode() {
        return this.moduleSimulateModeSubject.value;
    }
    get moduleAllowResubmission() {
        return this.moduleAllowResubmissionSubject.value;
    }
    get selectedModuleID() {
        const doubleQuoteRe = new RegExp("\"", "gi");
        if (this._selectedModuleID) {
            return this._selectedModuleID.toString().replace(doubleQuoteRe, "");
        }
        return this.sessionStorage.get(SessionStorageEnum.SelectedModuleID)?.replace(doubleQuoteRe, "");
    }
    selectedOrganisationID(organisationId) {
        this.selectedOrgIdSubject.next(organisationId);
    }
    setModuleSimulateMode() {
        const userInformationService = this.injector.get(UserInformationService);
        const module = userInformationService.getModuleInformation(this._selectedModuleID);
        if (module !== undefined && module !== null && Object.prototype.hasOwnProperty.call(module, "simulationMode")) {
            this.moduleSimulateModeSubject.next(module.simulationMode);
        }
    }
    setModuleAllowResubmission() {
        const userInformationService = this.injector.get(UserInformationService);
        const module = userInformationService.getModuleInformation(this._selectedModuleID);
        if (module !== undefined && module !== null && Object.prototype.hasOwnProperty.call(module, "enableResubmission")) {
            this.moduleAllowResubmissionSubject.next(module.enableResubmission);
        }
    }
    selectedSettingsHeader(header) {
        this.selectedSettingHeadingSubject.next(header);
    }
    getRequest(includeAppID = true, includeModuleID = true) {
        const request = JSON.parse(JSON.stringify(this.baseRequestObject));
        if (includeAppID) {
            request.moduleID = this._selectedModuleID;
            request.AppID = this.selectedApplicationID;
        }
        else if (includeModuleID) {
            request.moduleID = this._selectedModuleID;
        }
        return request;
    }
    // subscribe to alerts
    getAlert(alertId) {
        return this.alertSubject.asObservable().pipe(filter((x) => x && x.alertId === alertId));
    }
    showMessage(message, type, keepAfterRouteChange = false) {
        const alert = new Alert({ message, type });
        this.keepAfterRouteChange = alert.keepAfterRouteChange = keepAfterRouteChange;
        this.alertSubject.next(alert);
    }
    // clear alerts
    clearMessage(alertId) {
        this.alertSubject.next(new Alert({ alertId }));
    }
    showConsoleMessage(message) {
        if (isDevMode()) {
            console.log(message);
        }
    }
    setSelectedModule(value) {
        if (!value) {
            return;
        }
        if (this._selectedModuleID !== value) {
            this._selectedModuleID = value;
            this.setModuleSimulateMode();
            this.setModuleAllowResubmission();
            this.sessionStorage.set(SessionStorageEnum.SelectedModuleID, value.toString());
        }
    }
    handleServerErrorMessage(objectName, httpErrorResponse, operation) {
        switch (httpErrorResponse.status) {
            case +HttpStatusCodeEnum.UnAuthorized: {
                break;
            }
            case +HttpStatusCodeEnum.InternalServerError: {
                this.showMessage(objectName
                    ? `Something went wrong ${operation} the ${objectName.toLocaleLowerCase()}`
                    : "Something went wrong, please try again", AlertType.Error);
                break;
            }
            case +HttpStatusCodeEnum.BadRequest: {
                if (httpErrorResponse.error !== null) {
                    if (httpErrorResponse.error instanceof Blob) {
                        httpErrorResponse.error.text().then((result) => {
                            this.showMessage(result, AlertType.Warning);
                        });
                        break;
                    }
                    else if (typeof httpErrorResponse.error === "string") {
                        this.showMessage(httpErrorResponse.error.toString(), AlertType.Warning);
                        break;
                    }
                    else if (Object.prototype.hasOwnProperty.call(httpErrorResponse.error, "value") && httpErrorResponse.error.value !== undefined) {
                        this.showMessage(httpErrorResponse.error.value, AlertType.Warning);
                    }
                    else if (Object.prototype.hasOwnProperty.call(httpErrorResponse.error, "message") && httpErrorResponse.error.message !== undefined) {
                        this.showMessage(httpErrorResponse.error.message, AlertType.Warning);
                    }
                    else if (Object.prototype.hasOwnProperty.call(httpErrorResponse.error, "error") && Object.prototype.hasOwnProperty.call(httpErrorResponse.error.error, "message") &&
                        httpErrorResponse.error.error.message !== undefined) {
                        this.showMessage(httpErrorResponse.error.error.message, AlertType.Warning);
                    }
                    else if (Object.prototype.hasOwnProperty.call(httpErrorResponse.error, "error") && Object.prototype.hasOwnProperty.call(httpErrorResponse.error.error, "value") &&
                        httpErrorResponse.error.error.value !== undefined) {
                        this.showMessage(httpErrorResponse.error.error.value, AlertType.Warning);
                    }
                    else if (Object.prototype.hasOwnProperty.call(httpErrorResponse.error, "errors") && httpErrorResponse.error.errors !== undefined) {
                        try {
                            const errors = [];
                            const keys = Object.keys(httpErrorResponse.error.errors);
                            keys.forEach(key => {
                                const error = httpErrorResponse.error.errors[key];
                                error.forEach(errorValue => {
                                    errors.push(errorValue);
                                });
                            });
                            this.showMessage(errors.join(" "), AlertType.Warning);
                        }
                        catch (e) {
                            // this should never swallow errors
                            this.showMessage(httpErrorResponse.message, AlertType.Warning);
                        }
                    }
                    else {
                        this.showMessage(httpErrorResponse.error, AlertType.Warning);
                    }
                }
                else {
                    this.showMessage(httpErrorResponse.message, AlertType.Warning);
                }
                break;
            }
            case +HttpStatusCodeEnum.Forbidden: {
                this.showMessage(`${SharedConstants.FORBIDDEN_MESSAGE}`, AlertType.Warning);
                break;
            }
            case +HttpStatusCodeEnum.NotFound: {
                this.showMessage(httpErrorResponse.message ? httpErrorResponse.message : `${SharedConstants.NOT_FOUND_MESSAGE}`, AlertType.Warning);
                break;
            }
            case +HttpStatusCodeEnum.Conflict: {
                this.showMessage(`${SharedConstants.CONFLICT_MESSAGE}`, AlertType.Warning);
                break;
            }
            default: {
                if (objectName !== undefined && objectName !== null && objectName !== "") {
                    throw new Error(`${SharedConstants.UNKNOWN_ERROR_MESSAGE} ${operation} the ${objectName.toLocaleLowerCase()}: `
                        + httpErrorResponse.message);
                }
                else {
                    throw new Error(`${SharedConstants.UNKNOWN_ERROR_MESSAGE}: ` + httpErrorResponse.message);
                }
            }
        }
    }
    getApplicationStatus(jobId) {
        const params = {
            $filter: `jobId eq ${jobId}`,
        };
        return this.http.get(ApiApplicationStatusConstants.URL, { params });
    }
    static ctorParameters = () => [
        { type: Router },
        { type: HttpClient },
        { type: Injector },
        { type: SessionStorageService }
    ];
};
AppControllerService = __decorate([
    Injectable()
], AppControllerService);
export { AppControllerService };
